import {Controller} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["preview", "form", "emailField", "fileField", "error", "saveButton", "deleteButton"];

  open(event) {
    const modal = document.getElementById("avatar-modal");
    const modalController = this.application.getControllerForElementAndIdentifier(modal, "avatar-modal");
    modalController.openWith(event.currentTarget, event.params);
  }

  openWith(trigger, {email, initials, color, deleteUrl, avatarUrl}) {
    this.currentTrigger = trigger;
    this.currentDeleteUrl = deleteUrl;
    this.currentInitials = initials;
    this.currentColor = color;

    this.emailFieldTarget.value = email;
    this.fileFieldTarget.value = "";

    const previewUrl = avatarUrl ? `${avatarUrl}?cacheBustKey=${Date.now()}` : "";

    this.updatePreview(previewUrl);

    this.hideError();

    WGModal.open(`#${this.element.id}`);
  }

  close() {
    WGModal.close(`#${this.element.id}`);
  }

  async save() {
    this.hideError();
    this.saveButtonTarget.disabled = true;

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: new FormData(this.formTarget),
        credentials: "same-origin"
      });

      const data = await response.json();

      if (!response.ok) {
        this.showError(data?.errors, I18n.t("js.avatars.upload_error"));
        return;
      }

      if (data?.url) this.updateTriggerAvatar(data.url);

      this.close();
    } catch {
      this.showError(null, I18n.t("js.avatars.upload_error"));
    } finally {
      this.saveButtonTarget.disabled = false;
    }
  }

  async delete() {
    this.hideError();
    this.deleteButtonTarget.disabled = true;

    try {
      const csrfToken = document.querySelector("meta[name=\"csrf-token\"]").content;

      const response = await fetch(this.currentDeleteUrl, {
        method: "DELETE",
        headers: {"X-CSRF-Token": csrfToken},
        credentials: "same-origin"
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        this.showError(data?.errors, I18n.t("js.avatars.delete_error"));
        return;
      }

      this.updateTriggerAvatar(null);

      this.updatePreview(null);
      this.fileFieldTarget.value = "";
      this.close();
    } catch {
      this.showError(null, I18n.t("js.avatars.delete_error"));
    } finally {
      this.deleteButtonTarget.disabled = false;
    }
  }

  openFileSelector() {
    this.fileFieldTarget.click();
  }

  previewFile() {
    const file = this.fileFieldTarget.files?.[0];
    if (!file) return;

    const objectUrl = URL.createObjectURL(file);
    this.updatePreview(objectUrl);
  }

  updateTriggerAvatar(url) {
    const img = this.currentTrigger.querySelector("img");
    img?.remove();
    if (url) {
      const newImg = document.createElement("img");
      newImg.src = url;
      this.currentTrigger.querySelector(".wg-avatar")?.appendChild(newImg);
    }
  }

  showError(errors, fallback) {
    this.errorTarget.textContent = errors?.join(", ") || fallback;
    this.errorTarget.hidden = false;
  }

  hideError() {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  updatePreview(avatarUrl) {
    this.previewTarget.querySelector(".wg-avatar__initials").textContent = this.currentInitials;

    const avatarEl = this.previewTarget.querySelector(".wg-avatar");
    avatarEl.style.setProperty("--avatar-background-color", this.currentColor);

    let imageEl = this.previewTarget.querySelector("img");
    if (!imageEl && avatarUrl) {
      imageEl = document.createElement("img");
      this.previewTarget.querySelector(".wg-avatar")?.appendChild(imageEl);
    }

    if (imageEl) {
      if (avatarUrl) {
        imageEl.src = avatarUrl;
        imageEl.style.display = "block";
      } else {
        imageEl.src = "";
        imageEl.style.display = "none";
      }
    }
  }
}
